import { type FormEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';

import type { Supplier, TariffItem } from '@/shared/tariffs/types';
import { useSupplier, useTariffSearch } from '@/shared/tariffs/useTariffs';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Card } from '@/shared/ui/Card';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { ArrowLeftIcon, SearchIcon, XIcon } from '@/shared/ui/icons';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Segmented } from '@/shared/ui/Segmented';
import { Spinner } from '@/shared/ui/Spinner';
import { useDebounced } from '@/shared/utils/useDebounced';

import { catalogsApi } from './api';
import { ArticleDialog, ArticlesTable } from './Articles';
import { ConditionsSummary } from './Conditions';
import { NewVersionError, type OpenedCatalog, openCatalog } from './pdfEngine';
import {
  type Catalog,
  catalogFacts,
  catalogFailureTitle,
  MIN_SELECTION,
  selectionQuery,
} from './types';
import { useCatalog } from './useCatalogs';

/**
 * A supplier's catalog (contract v10.49, ADR 0044 of the back): the PDF read in pieces from
 * the bucket, and beside it what the tariffs of **that supplier** say of what is selected
 * in it, with its conditions. On a phone the search is a sheet that comes up from below.
 *
 * «Todos los proveedores» (ADR 0056 of the back) looks for it in the tariffs in force of
 * every supplier: a brand's catalog may have its tariff under the distributor's name, and
 * the same reference can be cheaper elsewhere. Same search, without
 * `supplier_id`; no contract change.
 */
export function CatalogViewerPage() {
  const { catalogId } = useParams();
  const catalog = useCatalog(catalogId);

  const backTo = catalog.data
    ? `/documentos/proveedores/${catalog.data.supplier_id}`
    : '/documentos';
  const back = (
    <Link to={backTo} className="text-ink-3 hover:text-ink inline-flex items-center gap-1">
      <ArrowLeftIcon size={14} /> {catalog.data?.supplier_name || 'Documentos'}
    </Link>
  );

  // What came first is what is shown: a refresh that fails while the catalog is open (the
  // line drops, the tab comes back) must not close it.
  const data = catalog.data;
  if (!data) {
    if (catalog.isError) {
      return (
        <>
          <PageHeader title="Catálogo" back={back} />
          <ErrorMessage error={catalog.error}>No se pudo cargar el catálogo</ErrorMessage>
        </>
      );
    }
    return <Spinner variant="block" label="Cargando el catálogo" />;
  }
  if (data.status !== 'ready') {
    return (
      <>
        <PageHeader title={data.name} back={back} description={data.supplier_name} />
        <NotReady catalog={data} />
      </>
    );
  }
  // Another catalog is another reader: its page, its selection and its attempts start over.
  return <Reader key={data.id} catalog={data} back={back} />;
}

function NotReady({ catalog }: { catalog: Catalog }) {
  if (catalog.status === 'failed') {
    return (
      <Callout tone="danger" title={catalogFailureTitle(catalog.error_code)}>
        {catalog.error ?? 'No se pudo preparar el catálogo.'}{' '}
        {catalog.error_code ? (
          <span className="text-ink-3 text-[12.5px]">({catalog.error_code})</span>
        ) : null}
      </Callout>
    );
  }
  return (
    <Card>
      <Spinner label={catalog.status === 'uploading' ? 'Subiéndose' : 'Preparando el catálogo'} />
      <p className="text-ink-3 mt-2 text-[13px]">
        {catalog.stuck
          ? 'Lleva demasiado tiempo. Quien gestiona los documentos puede pedirlo otra vez desde la ficha del proveedor.'
          : 'Se cuentan sus páginas y se mira si tiene texto. Un catálogo grande tarda un minuto.'}
      </p>
    </Card>
  );
}

/** Where the search beside the catalog looks: this supplier's tariffs or everyone's. */
type Scope = 'supplier' | 'all';

interface Selection {
  text: string;
  /** Each selection is new, the same reference twice too: the search starts from it again
   * even if something else was typed in between. */
  count: number;
}

function Reader({ catalog, back }: { catalog: Catalog; back: ReactNode }) {
  const supplier = useSupplier(catalog.supplier_id);
  const [selected, setSelected] = useState<Selection>({ text: '', count: 0 });
  const [sheet, setSheet] = useState(false);
  // On a phone a selection offers the search and does not open it: the sheet would come up
  // over the page while the finger is still moving the handles.
  const [offered, setOffered] = useState<string | null>(null);
  // Opened again after a piece that did not come, at the page it was on.
  const [attempt, setAttempt] = useState(0);
  // Here and not in the search: each selection is a new search, and the choice stays.
  const [scope, setScope] = useState<Scope>('supplier');
  const lastPage = useRef(1);

  const lookup = (
    <Lookup
      key={selected.count}
      initial={selected.text}
      supplier={supplier.data}
      supplierId={catalog.supplier_id}
      scope={scope}
      onScope={setScope}
    />
  );

  return (
    <>
      <PageHeader
        title={catalog.name}
        back={back}
        description={`${catalog.supplier_name} · ${catalogFacts(catalog)}`}
      />
      {catalog.has_text === false ? (
        <Callout tone="info" compact>
          Este catálogo está escaneado: no se puede seleccionar el texto. Escribe la referencia en
          el buscador.
        </Callout>
      ) : null}
      <div className="flex flex-col gap-4 lg:grid lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start">
        <PdfPane
          key={attempt}
          catalog={catalog}
          startPage={lastPage.current}
          onPage={(page) => {
            lastPage.current = page;
          }}
          onSelect={(text) => {
            setSelected((previous) => ({ text, count: previous.count + 1 }));
            setOffered(text);
          }}
          offered={sheet ? null : offered}
          onOpenSearch={() => {
            setOffered(null);
            setSheet(true);
          }}
          onReopen={() => setAttempt((value) => value + 1)}
        />
        <aside aria-label="Buscar en las tarifas" className="hidden lg:block">
          <Card>{lookup}</Card>
        </aside>
      </div>
      {sheet ? (
        <section
          aria-label="Buscar en las tarifas"
          className="bg-card border-line fixed inset-x-0 bottom-0 z-40 flex max-h-[75dvh] flex-col rounded-t-2xl border-t pb-[env(safe-area-inset-bottom)] shadow-lg lg:hidden"
        >
          <div className="flex items-center justify-between px-4 pt-3 pb-1">
            <p className="text-ink text-[15px] font-semibold">
              {scope === 'all'
                ? 'En las tarifas de todos los proveedores'
                : `En las tarifas de ${catalog.supplier_name}`}
            </p>
            <button
              type="button"
              aria-label="Cerrar"
              className="text-ink-3 hover:text-ink inline-flex size-9 items-center justify-center rounded-md"
              onClick={() => {
                setSheet(false);
                setOffered(null);
              }}
            >
              <XIcon size={18} />
            </button>
          </div>
          <div className="overflow-y-auto px-4 pb-4">{lookup}</div>
        </section>
      ) : null}
    </>
  );
}

/**
 * The search beside the catalog: what was selected, or what is typed, in the tariffs in
 * force of this supplier or of every supplier, and this supplier's conditions under it.
 */
function Lookup({
  initial,
  supplier,
  supplierId,
  scope,
  onScope,
}: {
  initial: string;
  supplier: Supplier | undefined;
  supplierId: string;
  scope: Scope;
  onScope: (scope: Scope) => void;
}) {
  const [typed, setTyped] = useState(initial);
  const [open, setOpen] = useState<TariffItem | null>(null);
  const q = useDebounced(typed, 300).trim();
  const everywhere = scope === 'all';
  const inForce = (supplier?.price_lists ?? []).some((list) => list.current);
  const asking = q.length >= MIN_SELECTION && (everywhere || inForce);
  const results = useTariffSearch(everywhere ? { q } : { q, supplier_id: supplierId }, asking);
  const items = results.data?.items ?? [];
  const lookEverywhere = (
    <Button variant="secondary" size="sm" className="self-start" onClick={() => onScope('all')}>
      Buscar en todos los proveedores
    </Button>
  );

  return (
    <div className="flex flex-col gap-3">
      <Segmented
        label="Dónde buscar"
        value={scope}
        onChange={onScope}
        options={[
          { value: 'supplier', label: 'Este proveedor' },
          { value: 'all', label: 'Todos los proveedores' },
        ]}
      />
      <div className="relative">
        <SearchIcon
          size={16}
          className="text-ink-4 pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
        />
        <input
          type="search"
          aria-label="Referencia o descripción"
          placeholder="Selecciona una referencia o escríbela"
          className="field w-full pl-9"
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
        />
      </div>

      {supplier && !inForce && !everywhere ? (
        <>
          <p className="text-ink-3 text-[13.5px]">
            Este proveedor no tiene ninguna tarifa vigente: no hay dónde buscar los precios.
          </p>
          {lookEverywhere}
        </>
      ) : null}
      {asking && results.isPending ? <Spinner label="Buscando" /> : null}
      {results.isError ? (
        <ErrorMessage error={results.error}>No se pudo buscar en las tarifas</ErrorMessage>
      ) : null}
      {asking && results.data && items.length === 0 ? (
        <>
          <p className="text-ink-3 text-[13.5px]">
            {everywhere
              ? `Nada con «${q}» en las tarifas vigentes de ningún proveedor.`
              : `Nada con «${q}» en sus tarifas vigentes.`}{' '}
            Prueba con el principio de la referencia o con menos palabras.
          </p>
          {everywhere ? null : lookEverywhere}
        </>
      ) : null}
      {asking && items.length > 0 ? (
        <>
          <ArticlesTable
            compact
            addable
            items={items}
            label="Artículos de la tarifa"
            onOpen={setOpen}
          />
          {results.data?.limited ? (
            <p className="text-ink-3 text-[13px]">
              Salen los {items.length} primeros: afina la búsqueda para ver el resto.
            </p>
          ) : null}
        </>
      ) : null}
      {!asking && (everywhere || inForce) ? (
        <p className="text-ink-3 text-[13.5px]">
          Selecciona una referencia en el catálogo y sale aquí su precio.
        </p>
      ) : null}

      {supplier ? (
        <div className="border-line flex flex-col gap-1.5 border-t pt-3">
          <p className="text-ink-2 text-[13px] font-semibold">
            {everywhere ? `Condiciones de ${supplier.name}` : 'Condiciones'}
          </p>
          <ConditionsSummary supplier={supplier} />
        </div>
      ) : null}
      {open ? <ArticleDialog addable item={open} onClose={() => setOpen(null)} /> : null}
    </div>
  );
}

type PaneState =
  { kind: 'loading' } | { kind: 'ready'; pages: number } | { kind: 'failed'; error: unknown };

/**
 * The PDF. pdf.js owns what is inside the box (`openCatalog`): this only gives it the box,
 * the buttons and what was selected. Keyed by attempt: opening it again is a new pane.
 */
function PdfPane({
  catalog,
  startPage,
  onPage,
  onSelect,
  offered,
  onOpenSearch,
  onReopen,
}: {
  catalog: Catalog;
  startPage: number;
  onPage: (page: number) => void;
  onSelect: (text: string) => void;
  /** What was selected, offered on a phone as a button over the page. */
  offered: string | null;
  onOpenSearch: () => void;
  onReopen: () => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const pages = useRef<HTMLDivElement>(null);
  const handle = useRef<OpenedCatalog | null>(null);
  const [state, setState] = useState<PaneState>({ kind: 'loading' });
  const [piece, setPiece] = useState<unknown>(null);
  // Only where it opens: the pane is keyed by attempt, and a later page from the parent must
  // not open the catalog again (selecting a reference re-renders the parent).
  const [openAt] = useState(startPage);
  const [page, setPage] = useState(startPage);
  const [jump, setJump] = useState('');
  const callbacks = useRef({ onPage, onSelect });
  callbacks.current = { onPage, onSelect };

  // Not server state: pdf.js is a viewer with a life of its own, opened on this box and
  // closed with it.
  useEffect(() => {
    const container = box.current;
    const viewer = pages.current;
    if (!container || !viewer) return undefined;
    const abort = new AbortController();
    openCatalog({
      container,
      viewer,
      newLink: () => catalogsApi.link(catalog.id),
      onFailure: (error) => setPiece(error),
      onPage: (current) => {
        setPage(current);
        callbacks.current.onPage(current);
      },
      startPage: openAt,
      signal: abort.signal,
    }).then(
      (opened) => {
        if (abort.signal.aborted) {
          opened.destroy();
          return;
        }
        handle.current = opened;
        setState({ kind: 'ready', pages: opened.pages });
      },
      (error: unknown) => {
        if (!abort.signal.aborted) setState({ kind: 'failed', error });
      },
    );
    return () => {
      abort.abort();
      handle.current?.destroy();
      handle.current = null;
    };
  }, [catalog.id, openAt]);

  // What is selected inside the catalog, once the hand stops: on a phone the selection
  // grows while the handles move.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const changed = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const selection = document.getSelection();
        if (!selection || selection.isCollapsed || !selection.anchorNode) return;
        if (!box.current?.contains(selection.anchorNode)) return;
        const text = selectionQuery(selection.toString());
        if (text.length >= MIN_SELECTION) callbacks.current.onSelect(text);
      }, 450);
    };
    document.addEventListener('selectionchange', changed);
    return () => {
      document.removeEventListener('selectionchange', changed);
      clearTimeout(timer);
    };
  }, []);

  const ready = state.kind === 'ready';
  const total = ready ? state.pages : (catalog.pages ?? 0);

  function goTo(event: FormEvent) {
    event.preventDefault();
    const wanted = Number.parseInt(jump, 10);
    if (Number.isFinite(wanted)) handle.current?.goTo(wanted);
    setJump('');
  }

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <form onSubmit={goTo} className="text-ink-2 flex items-center gap-1.5 text-[13.5px]">
          <label htmlFor="catalog-page">Página</label>
          <input
            id="catalog-page"
            inputMode="numeric"
            className="field h-8 w-16 text-center tabular-nums"
            value={jump}
            placeholder={String(page)}
            disabled={!ready}
            onChange={(event) => setJump(event.target.value.replace(/\D/g, ''))}
          />
          <span className="tabular-nums">de {total || '…'}</span>
        </form>
        <div className="ml-auto flex items-center gap-1">
          <Button
            variant="secondary"
            size="sm"
            aria-label="Alejar"
            disabled={!ready}
            onClick={() => handle.current?.zoomOut()}
          >
            −
          </Button>
          <Button
            variant="secondary"
            size="sm"
            aria-label="Acercar"
            disabled={!ready}
            onClick={() => handle.current?.zoomIn()}
          >
            +
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={!ready}
            onClick={() => handle.current?.fitWidth()}
          >
            Ajustar
          </Button>
          <Button
            size="sm"
            className="lg:hidden"
            icon={<SearchIcon size={14} />}
            onClick={onOpenSearch}
          >
            Precios
          </Button>
        </div>
      </div>

      {piece ? (
        <Callout
          tone="warn"
          compact
          action={
            <Button size="sm" variant="secondary" onClick={onReopen}>
              Volver a abrirlo
            </Button>
          }
        >
          <ErrorText error={piece} />
        </Callout>
      ) : null}

      <div className="border-line bg-paper-2 relative h-[calc(100dvh-15rem)] min-h-[22rem] overflow-hidden rounded-lg border lg:h-[calc(100dvh-11rem)]">
        <div
          ref={box}
          data-testid="catalog-pages"
          // Focusable, so the arrows and the page keys scroll it without a mouse.
          tabIndex={0}
          role="region"
          aria-label={catalog.name}
          className="catalog-viewer focus-visible:outline-accent absolute inset-0 overflow-auto focus-visible:outline-2"
        >
          <div ref={pages} className="pdfViewer" />
        </div>
        {offered ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center px-3 lg:hidden">
            <Button
              size="sm"
              className="pointer-events-auto max-w-full overflow-hidden shadow-lg"
              icon={<SearchIcon size={14} />}
              onClick={onOpenSearch}
            >
              <span className="min-w-0 truncate">Precio de «{offered}»</span>
            </Button>
          </div>
        ) : null}
        {state.kind === 'loading' ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <Spinner label="Abriendo el catálogo" />
          </div>
        ) : null}
        {state.kind === 'failed' ? (
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <Callout
              tone="danger"
              title="No se pudo abrir"
              action={
                state.error instanceof NewVersionError ? (
                  <Button size="sm" onClick={() => window.location.reload()}>
                    Recargar la página
                  </Button>
                ) : (
                  <Button size="sm" onClick={onReopen}>
                    Volver a intentarlo
                  </Button>
                )
              }
            >
              <ErrorText error={state.error} />
            </Callout>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ErrorText({ error }: { error: unknown }) {
  return <ErrorMessage error={error} tone="warning" className="border-0 bg-transparent p-0" />;
}
